import { Check } from 'lucide-react'
import { accents, palettes } from '@/lib/theme/theme-options'
import { setThemePreferences } from '@/lib/theme/theme-store'
import { useTheme } from '@/lib/theme/use-theme'

export function ThemePalettePicker() {
  const theme = useTheme()
  return (
    <>
      <fieldset className="m-0 border-0 border-b border-solid border-border-default px-3 pb-3 pt-2.5">
        <legend className="sr-only">Accent</legend>
        <div aria-hidden="true" className="text-xs font-semibold text-muted">
          Accent
        </div>
        <div className="mt-2.5 flex items-center gap-4 px-1">
          {accents.map((accent) => (
            <button
              key={accent.id}
              type="button"
              aria-label={accent.label}
              title={accent.label}
              aria-pressed={theme.accent === accent.id}
              className={`theme-accent-choice grid size-8 place-items-center rounded-lg border bg-canvas transition-colors ${theme.accent === accent.id ? 'border-accent ring-2 ring-accent/50 ring-offset-2 ring-offset-raised' : 'border-border-strong hover:border-accent'}`}
              onClick={() => setThemePreferences({ accent: accent.id })}
            >
              <span
                data-accent={accent.id}
                aria-hidden="true"
                className="theme-accent-swatch flex items-center gap-1"
              >
                <span className="size-3.5 rounded-full bg-current" />
                <span className="flex flex-col gap-0.5">
                  <span className="size-1.5 rounded-full bg-current" />
                  <span className="size-1.5 rounded-full bg-current opacity-70" />
                </span>
              </span>
            </button>
          ))}
        </div>
      </fieldset>
      <fieldset className="m-0 min-w-0 border-0 px-2 pb-2 pt-2">
        <legend className="sr-only">Palette</legend>
        <div
          aria-hidden="true"
          className="mb-2 px-3 text-xs font-semibold text-muted"
        >
          Palette
        </div>
        <div className="space-y-0.5">
          {palettes.map((palette) => (
            <button
              key={palette.id}
              type="button"
              aria-label={palette.label}
              aria-pressed={theme.palette === palette.id}
              className={`flex w-full items-start gap-2.5 rounded-md px-2 py-2 text-left transition-colors ${theme.palette === palette.id ? 'bg-accent-solid/30' : 'hover:bg-hover'}`}
              onClick={() => setThemePreferences({ palette: palette.id })}
            >
              <span
                aria-hidden="true"
                data-preview-palette={palette.id}
                className={`relative mt-0.5 size-8 shrink-0 overflow-hidden rounded-md border ${theme.palette === palette.id ? 'border-accent' : 'border-border-strong'}`}
              >
                <span className="flex h-3.5">
                  <span
                    data-theme="light"
                    data-palette={palette.id}
                    className="theme-palette-paper flex w-1/2 items-center justify-center bg-raised"
                  >
                    <span className="size-2.5 rounded-[3px] border border-border-default bg-raised" />
                  </span>
                  <span
                    data-theme="dark"
                    data-palette={palette.id}
                    className="flex w-1/2 items-center justify-center bg-canvas"
                  >
                    <span className="size-2.5 rounded-[3px] border border-border-default bg-surface" />
                  </span>
                </span>
                <span className="absolute bottom-1.5 left-1.5 size-1.5 rounded-full bg-accent-solid" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between gap-2 text-[13px] font-semibold text-primary">
                  {palette.label}
                  {theme.palette === palette.id && (
                    <Check
                      size={14}
                      className="shrink-0 text-accent"
                      aria-hidden="true"
                    />
                  )}
                </span>
                <span className="mt-0.5 block pr-2 text-xs leading-4 text-muted">
                  {palette.description}
                </span>
              </span>
            </button>
          ))}
        </div>
      </fieldset>
    </>
  )
}
